import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Button from './Button/Button';
import Input from './Input/Input';
import Select from './Select/Select';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableRow({ id, children, ...props }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: isDragging ? '#e2e6ea' : undefined,
    userSelect: isDragging ? 'none' : undefined,
    cursor: 'grab',
  };

  return (
    <tr ref={setNodeRef} style={style} {...attributes} {...listeners} {...props}>
      {children}
    </tr>
  );
}

function SkillGroups({ skillGroups, reloadAll }) {
  const [editingSkillGroup, setEditingSkillGroup] = useState(null);
  const [localGroups, setLocalGroups] = useState(skillGroups);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');

  // Load departments on mount
  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const res = await api.getDepartments();
        setDepartments(res.data);
        if (res.data.length > 0) {
          setSelectedDepartmentId(res.data[0].id);
        }
      } catch (error) {
        console.error('Error loading departments:', error);
      }
    };
    
    loadDepartments();
  }, []);

  // Update local groups when skillGroups or selectedDepartmentId changes
  useEffect(() => {
    if (selectedDepartmentId) {
      const filtered = skillGroups.filter(group => group.department_id === parseInt(selectedDepartmentId));
      setLocalGroups(filtered);
    } else {
      setLocalGroups(skillGroups);
    }
  }, [skillGroups, selectedDepartmentId]);

  const handleUpdateSkillGroup = async (e) => {
    e.preventDefault();
    try {
      await api.updateSkillGroup(editingSkillGroup.id, editingSkillGroup);
      setEditingSkillGroup(null);
      reloadAll();
    } catch (error) {
      console.error('Error updating skill group:', error);
    }
  };

  const handleDeleteSkillGroup = async (id) => {
    if (window.confirm('Are you sure you want to delete this skill group?')) {
      try {
        await api.deleteSkillGroup(id);
        reloadAll();
      } catch (error) {
        console.error('Error deleting skill group:', error);
      }
    }
  };

  const handleUpdateColor = async (id, color) => {
    try {
      await api.updateSkillGroup(id, { color });
      reloadAll();
    } catch (error) {
      console.error('Error updating skill group color:', error);
    }
  };

  // Transform departments for Select component
  const departmentOptions = departments.map(dept => ({
    value: dept.id,
    label: dept.name
  }));

  // dnd-kit setup
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    
    const oldIndex = localGroups.findIndex(g => String(g.id) === String(active.id));
    const newIndex = localGroups.findIndex(g => String(g.id) === String(over.id));
    
    if (oldIndex === -1 || newIndex === -1) return; // Safety check
    
    const reordered = arrayMove(localGroups, oldIndex, newIndex);
    setLocalGroups(reordered);
    
    try {
      // Ensure we're sending numeric IDs in the correct format
      const orderIds = reordered.map(group => Number(group.id));
      
      console.log('Sending skill group order update:', {
        order: orderIds
      });
      
      // Send only the order array as expected by the backend
      await api.updateSkillGroupsOrder({
        order: orderIds
      });
      
      // Call reloadAll to update the skill groups in TableView
      reloadAll();
    } catch (error) {
      console.error('Error updating order:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
      }
      reloadAll(); // Only reload if there was an error
    }
  };

  return (
    <div>
      <div className="form-group mb-4">
        <label>Filter by Department</label>
        <Select
          options={departmentOptions}
          value={selectedDepartmentId}
          onChange={(value) => setSelectedDepartmentId(value)}
          placeholder="Select a department"
          id="department-filter"
          showSearch={false}
        />
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <div className="table-scroll-wrapper table-scroll-wrapper--inline">
          <table className="table">
            <thead>
              <tr>
              <th>Title</th>
              <th>Color</th>
              <th>Department</th>
              <th>Actions</th>
            </tr>
          </thead>
          <SortableContext
            items={localGroups.map(g => String(g.id))}
            strategy={verticalListSortingStrategy}
          >
            <tbody>
              {localGroups.map((group) => (
                <SortableRow key={group.id} id={String(group.id)}>
                  <td>
                    {editingSkillGroup?.id === group.id ? (
                      <Input
                        type="text"
                        value={editingSkillGroup.title}
                        onChange={(e) => setEditingSkillGroup({ ...editingSkillGroup, title: e.target.value })}
                        id={`edit-skill-group-title-${group.id}`}
                      />
                    ) : (
                      group.title
                    )}
                  </td>
                  <td>
                    {editingSkillGroup?.id === group.id ? (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {/* Using native color input for specialized color picking UI */}
                        <input
                          type="color"
                          value={editingSkillGroup.color.startsWith('hsl') 
                            ? hslToHex(editingSkillGroup.color) 
                            : editingSkillGroup.color}
                          onChange={(e) => {
                            const hexColor = e.target.value;
                            const hslColor = hexToHsl(hexColor);
                            setEditingSkillGroup({ ...editingSkillGroup, color: hslColor });
                          }}
                          style={{ width: '40px', height: '30px', padding: '0', cursor: 'pointer' }}
                        />
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {/* Using native color input for specialized color picking UI */}
                        <input
                          type="color"
                          value={group.color?.startsWith('hsl') 
                            ? hslToHex(group.color) 
                            : (group.color || '#e6f0fa')}
                          onChange={(e) => {
                            const hexColor = e.target.value;
                            const hslColor = hexToHsl(hexColor);
                            handleUpdateColor(group.id, hslColor);
                          }}
                          style={{ width: '40px', height: '30px', padding: '0', cursor: 'pointer' }}
                        />
                      </div>
                    )}
                  </td>
                  <td>
                    {editingSkillGroup?.id === group.id ? (
                      <Select
                        options={departmentOptions}
                        showSearch={false}
                        value={editingSkillGroup.department_id || ''}
                        onChange={(value) => setEditingSkillGroup({ ...editingSkillGroup, department_id: value })}
                        placeholder="Select a department"
                        id={`edit-skill-group-department-${group.id}`}
                      />
                    ) : (
                      departments.find(d => d.id === group.department_id)?.name || '-'
                    )}
                  </td>
                  <td className="td--actions">
                    {editingSkillGroup?.id === group.id ? (
                      <>
                        <Button type="primary" onClick={handleUpdateSkillGroup}>Save</Button>
                        <Button type="secondary" onClick={() => setEditingSkillGroup(null)}>Cancel</Button>
                      </>
                    ) : (
                      <>
                        <Button type="secondary" onClick={() => setEditingSkillGroup(group)}>Edit</Button>
                        <Button type="destructive" onClick={() => handleDeleteSkillGroup(group.id)}>Delete</Button>
                      </>
                    )}
                  </td>
                </SortableRow>
              ))}
            </tbody>
          </SortableContext>
          </table>
        </div>
      </DndContext>
    </div>
  );
}

// Helper function to convert HSL to HEX
function hslToHex(hslStr) {
  // Extract values from hsl string like "hsl(210, 50%, 95%)"
  const match = hslStr.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
  if (!match) return '#e6f0fa'; // Default color if parsing fails
  
  const h = parseInt(match[1], 10) / 360;
  const s = parseInt(match[2], 10) / 100;
  const l = parseInt(match[3], 10) / 100;

  let r, g, b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  const toHex = x => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// Helper function to convert HEX to HSL
function hexToHsl(hexStr) {
  // Remove the # if present
  let hex = hexStr.replace('#', '');
  
  // Convert to RGB
  let r = parseInt(hex.substring(0, 2), 16) / 255;
  let g = parseInt(hex.substring(2, 4), 16) / 255;
  let b = parseInt(hex.substring(4, 6), 16) / 255;
  
  // Find min and max
  let max = Math.max(r, g, b);
  let min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    let d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: h = 0;
    }
    h /= 6;
  }

  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);

  return `hsl(${h}, ${s}%, ${l}%)`;
}

export default SkillGroups; 